import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { AdminAudit } from '../components/admin/AdminAudit';
import { AdminCategories } from '../components/admin/AdminCategories';
import { AdminLimits } from '../components/admin/AdminLimits';
import { AdminOverview } from '../components/admin/AdminOverview';
import { AdminPeople } from '../components/admin/AdminPeople';
import { AdminTags } from '../components/admin/AdminTags';
import { AdminTemplates } from '../components/admin/AdminTemplates';
import { AppShell } from '../components/layout/AppShell';
import { EmptyState } from '../components/ui';
import './AdminPage.css';

const SECTIONS = [
  { key: 'overview', label: 'Overview', render: () => <AdminOverview /> },
  { key: 'people', label: 'People', render: () => <AdminPeople /> },
  { key: 'limits', label: 'Limits', render: () => <AdminLimits /> },
  { key: 'categories', label: 'Categories', render: () => <AdminCategories /> },
  { key: 'tags', label: 'Tags', render: () => <AdminTags /> },
  { key: 'templates', label: 'Templates', render: () => <AdminTemplates /> },
  { key: 'audit', label: 'Record', render: () => <AdminAudit /> },
] as const;

type Section = (typeof SECTIONS)[number];

/**
 * `/admin` — running the instance.
 *
 * The role check here is courtesy, not security: it keeps somebody who typed
 * the address from meeting seven failing panels. Every endpoint behind this
 * page asks again, and refuses again (Rules 7, 8).
 */
export function AdminPage() {
  const { account } = useAuth();
  const [params, setParams] = useSearchParams();
  const asked = params.get('section') ?? 'overview';
  const section: Section = SECTIONS.find((entry) => entry.key === asked) ?? SECTIONS[0];

  if (account && account.role !== 'ADMIN') {
    return (
      // The refusal is still a page, and a page has a name (Phase 29).
      <AppShell header={<h1>Administration</h1>}>
        <EmptyState
          title="This is for the people who run this instance"
          description="Ask an administrator if you need something changed here."
        />
      </AppShell>
    );
  }

  return (
    <AppShell
      header={
        <div className="sc-page-header">
          <div>
            <h1>Administration</h1>
            <p className="sc-admin__muted">
              What this instance offers, who may do what, and the record of every change.
            </p>
          </div>
        </div>
      }
    >
      <nav className="sc-admin__tabs" aria-label="Administration sections">
        {SECTIONS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            className={entry.key === section.key ? 'is-active' : ''}
            aria-current={entry.key === section.key ? 'page' : undefined}
            onClick={() => setParams(entry.key === 'overview' ? {} : { section: entry.key })}
          >
            {entry.label}
          </button>
        ))}
      </nav>

      <section className="sc-admin__panel" aria-label={section.label}>
        {section.render()}
      </section>
    </AppShell>
  );
}
